import React, { useState, useEffect, useRef } from 'react'
import { Box, Typography, Button } from '@mui/material';
import ChatBubble from './ChatBubble';
import ThinkingAccordion from './ThinkingAccordion';
import Footer from './Footer';
import useCentralDiscoveryQueue from '../hooks/useCentralDiscoveryQueue';
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue';
import { useDirectTopicCallback } from '../utils/directTopicCallback';
import { 
  SingleRowSelectorTemplate, 
  MultiRowSelectorTemplate, 
  DistributionBarChartTemplate, 
  InlineSliderTemplate 
} from './templates';
import { DISCOVERY_TEMPLATE_TYPES } from '../utils/discoveryQueueSchema';

/**
 * Simplified ChatInterface that uses direct callbacks instead of message-based approach
 * 
 * Flow:
 * 1. For completed queue items → render read-only components
 * 2. For pending queue items → render interactive components (one at a time)
 * 3. When item is confirmed → mark as complete and show next item
 * 4. When topic is complete → advance to next topic directly
 */
const ChatInterfaceQueueDrivenSimple = ({
  currentDiscoveryState,
  advanceToNextTopic,
  updateRequirements,
  getAllRequirements,
  handleRefreshResults,
  queries = {},
  sessionId,
  userId,
  setTitleKeys,
  setConfirmedSkills,
  discoveryProgress,
  onSendText,
  onSendVoice,
  onStopVoice,
  isRecording,
  isWaitingForResponse,
  convoStarted,
  handleStartIntake, // Add handleStartIntake prop
  messages = [], // Add messages prop to use backend messages
  setMessages // Add setMessages prop
}) => {
  const chatInterfaceRef = useRef();
  const scrollContainerRef = useRef(null);
  const preserveScrollPositionRef = useRef(false);
  
  // State for conversational flow
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingMessage, setThinkingMessage] = useState('');
  const [currentTopicIntroduced, setCurrentTopicIntroduced] = useState(false);
  const populationAttempted = useRef(new Set());
  const [isAdvancingTopic, setIsAdvancingTopic] = useState(false);
  // Bump this to force a re-render and drop memoized UI when cache is cleared
  const [cacheBuster, setCacheBuster] = useState(0);
  
  // Debug sessionId prop
  console.log('🔍 ChatInterfaceQueueDrivenSimple sessionId prop:', sessionId);
  
  // Use direct topic callback instead of message-based approach
  const {
    initializeCurrentTopic,
    advanceToNextTopicDirect,
    populateTopicQueue,
    getCurrentTopicProgress,
    isTopicComplete
  } = useDirectTopicCallback({
    currentDiscoveryState,
    advanceToNextTopic,
    updateRequirements,
    getAllRequirements,
    handleRefreshResults,
    queries,
    sessionId,
    userId,
    setTitleKeys,
    setConfirmedSkills,
    discoveryProgress,
    chatInterfaceRef,
    onSetTyping: (isTyping, message) => {
      console.log('🧠 ChatInterface onSetTyping called:', { isTyping, message });
      setIsThinking(isTyping);
      setThinkingMessage(message || '');
    }
  });

  // Use central discovery queue
  const {
    queueItems,
    isLoading: queueLoading,
    error: queueError,
    getItemsByTopic,
    markAsDiscovered,
    progressPercentage,
    topicStats
  } = useCentralDiscoveryQueue(sessionId || null, []);

  // Use backend messages instead of custom welcome message
  // The backend's generateNextStepsMessage provides the detailed intake summary
  const displayMessages = React.useMemo(() => {
    // Include both user and AI messages for complete conversation
    // Filter out placeholder artifacts like "Conversation captured."
    const isPlaceholder = (s) => {
      const t = (s || '').toString().trim().toLowerCase();
      return (
        !t ||
        t === 'conversation captured.' ||
        t === 'conversation captured' ||
        t === 'user: conversation captured.' ||
        t === 'user: conversation captured' ||
        t === 'agent: conversation captured.' ||
        t === 'agent: conversation captured'
      );
    };
    return messages.filter(
      (msg) =>
        (msg.role === 'AI' || msg.role === 'User') && !isPlaceholder(msg.content)
    );
  }, [messages]);

  // Clear local UI state (including read-only render state) when app cache is cleared
  useEffect(() => {
    const handleClear = async () => {
      try {
        console.log('🧹 ChatInterfaceQueueDrivenSimple: clearing UI cache on app:clear-cache');
        // Reset ephemeral UI state
        setIsThinking(false);
        setThinkingMessage('');
        setCurrentTopicIntroduced(false);
        setIsAdvancingTopic(false);
        populationAttempted.current.clear();
        preserveScrollPositionRef.current = false;

        // Persistently clear discovery queue for this session to avoid backend re-population on reload
        try { await centralDiscoveryQueue.resetForSession(sessionId || centralDiscoveryQueue.sessionId); } catch (_) {}

        // Force a re-render tick to drop any memoized derived data
        setCacheBuster(Date.now());
      } catch (e) {
        console.warn('UI cache clear handler error:', e);
      }
    };

    window.addEventListener('app:clear-cache', handleClear);
    return () => window.removeEventListener('app:clear-cache', handleClear);
  }, [sessionId]);

  // Add topic introduction messages and initialize topic
  useEffect(() => {
    // Gate queue population/render init while JD intake request is in-flight
    if (typeof window !== 'undefined' && window.__jdInFlight === true) {
      console.log('⏳ JD intake in-flight - skipping topic queue initialization/population');
      return;
    }
    const currentTopic = currentDiscoveryState?.currentDiscoTopic;
    
    console.log('🎯 SIMPLE INTERFACE - useEffect triggered:', {
      currentTopic,
      sessionId,
      convoStarted,
      discoveryState: currentDiscoveryState
    });
    
    // Handle case when returning from candidate_preview or currentTopic is null
    if ((!currentTopic || currentTopic === 'candidate_preview') && sessionId && convoStarted && currentDiscoveryState) {
      console.log('🎯 SIMPLE INTERFACE - No current topic or returning from candidate_preview, determining next topic');
      
      const { discoveryOrder, completedTopics } = currentDiscoveryState;
      
      // Also check queue items to determine what's actually completed
      const allQueueItems = queueItems || [];
      const completedQueueTopics = [...new Set(
        allQueueItems
          .filter(item => item.disco_done === true)
          .map(item => item.topic)
      )];
      
      console.log('🎯 SIMPLE INTERFACE - Discovery analysis:', {
        discoveryOrder,
        completedTopics: completedTopics || [],
        completedQueueTopics,
        allQueueItems: allQueueItems.map(item => ({ 
          topic: item.topic, 
          section_id: item.section_id, 
          disco_done: item.disco_done 
        }))
      });
      
      if (discoveryOrder && discoveryOrder.length > 0) {
        // Find the next uncompleted topic based on queue items (more accurate than completedTopics)
        const nextTopic = discoveryOrder.find(topic => 
          topic !== 'candidate_preview' && !completedQueueTopics.includes(topic)
        );
        
        // If no uncompleted topics found, check if we have any pending queue items
        const pendingQueueItems = allQueueItems.filter(item => item.disco_done !== true);
        const nextTopicFromPending = pendingQueueItems.length > 0 ? pendingQueueItems[0].topic : null;
        
        const finalNextTopic = nextTopic || nextTopicFromPending;
        
        console.log('🎯 SIMPLE INTERFACE - Determined next topic:', {
          nextTopic,
          nextTopicFromPending,
          finalNextTopic,
          pendingQueueItems: pendingQueueItems.map(item => ({ topic: item.topic, section_id: item.section_id }))
        });
        
        if (finalNextTopic) {
          // Use advanceToNextTopic to transition to the next topic
          console.log('🎯 SIMPLE INTERFACE - Transitioning to next topic via advanceToNextTopic');
          advanceToNextTopic();
          return; // Exit early, let the effect run again with the new topic
        }
      }
    }
    
    if (currentTopic && sessionId && convoStarted) {
      console.log('🎯 SIMPLE INTERFACE - Topic changed, initializing:', currentTopic);
      
      // Add topic introduction message
      const topicMessages = {
        skills: "Now let's identify the key skills and qualifications you're looking for in candidates.",
        experience: "Great! Now let's determine the ideal experience level for this role.",
        location: "Let's specify the locations where you'd like to find candidates.",
        title: "Now let's identify the job titles that would be a good fit for this role.",
        company: "Let's think about the types of companies or company characteristics that would be relevant.",
        education: "Finally, let's consider any educational requirements for this position."
      };
      
      const introMessage = topicMessages[currentTopic] || `Let's work on the ${currentTopic} requirements.`;
      
      // Skip topic intro messages since backend API already provides comprehensive discovery messages
      // The backend /api/role-type-selection endpoint provides detailed discovery sequence messages
      // that include topic introductions, so we don't need to add redundant messages here
      console.log('🎯 SIMPLE INTERFACE - Skipping frontend topic intro - backend provides discovery messages');
      setCurrentTopicIntroduced(true);
      
      // Check if topic already has queue items using comprehensive check
      const allQueueItems = queueItems || [];
      const topicItems = allQueueItems.filter(item => item.topic === currentTopic);
      
      console.log('🎯 SIMPLE INTERFACE - Topic items check (comprehensive):', {
        currentTopic,
        existingItems: topicItems.length,
        totalQueueItems: allQueueItems.length,
        items: topicItems.map(item => ({ section_id: item.section_id, disco_done: item.disco_done, topic: item.topic })),
        populationAttempted: populationAttempted.current.has(currentTopic),
        isAdvancingTopic,
        willPopulateQueue: topicItems.length === 0 && !populationAttempted.current.has(currentTopic) && !isAdvancingTopic
      });
      
      if (topicItems.length === 0 && !populationAttempted.current.has(currentTopic) && !isAdvancingTopic) {
        console.log('🎯 SIMPLE INTERFACE - No queue items, populating for topic:', currentTopic);
        
        // Mark this topic as attempted
        populationAttempted.current.add(currentTopic);
        
        // Populate queue (typing indicators handled automatically by DirectTopicCallback)
        populateTopicQueue(currentTopic).then(() => {
          console.log('🎯 SIMPLE INTERFACE - Queue population completed for:', currentTopic);
        }).catch((error) => {
          console.error('🎯 SIMPLE INTERFACE - Queue population failed for:', currentTopic, error);
          setIsThinking(false);
          setThinkingMessage('');
        });
      } else {
        console.log('🎯 SIMPLE INTERFACE - Topic already has queue items:', topicItems.length);
        
        // Check if all topic items are completed
        const completedItems = topicItems.filter(item => item.disco_done === true);
        const isTopicFullyCompleted = completedItems.length === topicItems.length && topicItems.length > 0;
        
        console.log('🎯 SIMPLE INTERFACE - Topic completion check:', {
          currentTopic,
          totalItems: topicItems.length,
          completedItems: completedItems.length,
          isTopicFullyCompleted,
          items: topicItems.map(item => ({ section_id: item.section_id, disco_done: item.disco_done }))
        });
        
        // If topic is fully completed and not candidate_preview, auto-advance to next topic
        if (isTopicFullyCompleted && currentTopic !== 'candidate_preview') {
          console.log('🎯 SIMPLE INTERFACE - Topic fully completed, auto-advancing to next topic');
          
          // Use advanceToNextTopic to move to the next topic
          try {
            const nextState = advanceToNextTopic();
            console.log('🎯 SIMPLE INTERFACE - Advanced to next topic:', nextState?.currentDiscoTopic);
          } catch (error) {
            console.error('🎯 SIMPLE INTERFACE - Error advancing to next topic:', error);
          }
        }
        
        // 🔧 DEBUG: Special handling for title topic to force population if it's stuck
        if (currentTopic === 'title' && topicItems.length === 0 && !populationAttempted.current.has(currentTopic)) {
          console.log('🏷️ SIMPLE INTERFACE - FORCE POPULATING TITLE TOPIC (debug workaround)');
          populationAttempted.current.add(currentTopic);
          
          populateTopicQueue(currentTopic).then(() => {
            console.log('🏷️ SIMPLE INTERFACE - FORCE population completed for title');
          }).catch((error) => {
            console.error('🏷️ SIMPLE INTERFACE - FORCE population failed for title:', error);
            
            // 🔧 FALLBACK: Create a basic title queue item directly if strategy fails
            console.log('🏷️ SIMPLE INTERFACE - Creating fallback title queue item directly');
            const fallbackTitleItem = {
              queue_id: `title_fallback_${Date.now()}`,
              section_id: 'job_title_manual',
              section_name: 'Job Title',
              section_type: 'Must Have',
              topic: 'title',
              disco_done: false,
              skipped: false,
              bool_str: '',
              
              template_type: 'ConversationTemplate',
              ai_intro_message: "What job title are you looking to fill? (e.g., 'Senior Software Engineer', 'Product Manager', 'Data Scientist')",
              ai_confirmation_message: "Perfect! I've captured your job title requirements.",
              
              ux_config: {
                title: "Job Title Definition",
                conversation_mode: true,
                ro_footer: false
              },
              
              template_props: {
                conversation_prompt: "Please enter the job title you're hiring for:",
                placeholder: "e.g., Senior Software Engineer, Marketing Manager..."
              },
              
              created_at: Date.now(),
              updated_at: Date.now()
            };
            
            centralDiscoveryQueue.addQueueItem(fallbackTitleItem).then(() => {
              console.log('🏷️ SIMPLE INTERFACE - Fallback title queue item created successfully');
            }).catch((fallbackError) => {
              console.error('🏷️ SIMPLE INTERFACE - Failed to create fallback title item:', fallbackError);
            });
          });
        }
      }
    }
  }, [currentDiscoveryState, sessionId, convoStarted, currentTopicIntroduced]);

  // Get current topic queue items - with enhanced debugging
  const currentTopicItems = React.useMemo(() => {
    const currentTopic = currentDiscoveryState?.currentDiscoTopic;
    
    if (!currentTopic) {
      return { allItems: [], completedItems: [], pendingItems: [], nextPendingItem: null };
    }

    const topicItems = getItemsByTopic(currentTopic);
    const completedItems = topicItems.filter(item => item.disco_done === true);
    const skippedItems = topicItems.filter(item => item.skipped === true);
    const pendingItems = topicItems.filter(item => item.disco_done !== true && item.skipped !== true);
    const nextPendingItem = pendingItems.length > 0 ? pendingItems[0] : null;


    return {
      allItems: topicItems,
      completedItems,
      skippedItems,
      pendingItems,
      nextPendingItem
    };
  }, [queueItems, currentDiscoveryState?.currentDiscoTopic, getItemsByTopic, cacheBuster]);

  // Auto-advance to next topic if current topic has no pending items
  useEffect(() => {
    const currentTopic = currentDiscoveryState?.currentDiscoTopic;
    
    // Only auto-advance if we have a current topic, session is started, and we're not advancing already
    if (currentTopic && sessionId && convoStarted && !isAdvancingTopic) {
      const { nextPendingItem, allItems } = currentTopicItems;
      
      // If current topic has items but no pending items, advance to next topic
      if (allItems.length > 0 && !nextPendingItem) {
        console.log('🎯 AUTO-ADVANCE: Current topic has no pending items, advancing to next topic:', {
          currentTopic,
          totalItems: allItems.length,
          completedItems: currentTopicItems.completedItems.length,
          pendingItems: currentTopicItems.pendingItems.length
        });
        
        // Advance to next topic
        try {
          advanceToNextTopic();
          console.log('✅ AUTO-ADVANCE: Successfully advanced to next topic');
        } catch (error) {
          console.error('❌ AUTO-ADVANCE: Error advancing to next topic:', error);
        }
      }
    }
  }, [currentTopicItems, currentDiscoveryState?.currentDiscoTopic, sessionId, convoStarted, isAdvancingTopic, advanceToNextTopic]);

  // Scroll preservation effect - store previous scroll position
  const previousScrollTop = useRef(0);
  
  useEffect(() => {
    if (preserveScrollPositionRef.current && scrollContainerRef.current) {
      // Restore the previous scroll position
      const previousPosition = previousScrollTop.current;
      scrollContainerRef.current.scrollTop = previousPosition;
      preserveScrollPositionRef.current = false;
      console.log('🔄 Restored scroll position to:', previousPosition);
      return;
    }
    
    if (scrollContainerRef.current && !preserveScrollPositionRef.current) {
      // Store current scroll position before any updates
      previousScrollTop.current = scrollContainerRef.current.scrollTop;
      
      // Only auto-scroll to bottom for new content if user is already near the bottom
      const container = scrollContainerRef.current;
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      
      if (isNearBottom) {
        setTimeout(() => {
          if (scrollContainerRef.current) {
            const newScrollTop = scrollContainerRef.current.scrollHeight;
            scrollContainerRef.current.scrollTop = newScrollTop;
            previousScrollTop.current = newScrollTop;
            console.log('📍 Auto-scrolled to bottom:', newScrollTop);
          }
        }, 100);
      }
    }
  }, [displayMessages]);

  // 🔧 FIX: Auto-scroll to bottom functionality
  const scrollToBottom = React.useCallback((smooth = true) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
  }, []);
  
  // Auto-scroll when thinking state changes or new content appears
  React.useEffect(() => {
    if (isThinking || isWaitingForResponse) {
      // Scroll to bottom when thinking starts
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [isThinking, isWaitingForResponse, scrollToBottom]);
  
  // Auto-scroll when new queue items are added (but not when preserving position)
  React.useEffect(() => {
    if (!preserveScrollPositionRef.current) {
      // Only auto-scroll if we're not preserving scroll position
      setTimeout(() => scrollToBottom(), 200);
    }
  }, [currentTopicItems.allItems.length, scrollToBottom]);

  // Handle skipping discovery for a topic
  const handleSkipDiscovery = React.useCallback(async (queueItem) => {
    const currentTopic = queueItem.topic;
    console.log('🚫 SIMPLE INTERFACE - Skipping discovery for topic:', currentTopic);
    
    try {
      // Mark all pending items from this topic as skipped in the central queue
      const topicItems = getItemsByTopic(currentTopic);
      const pendingItems = topicItems.filter(item => !item.disco_done && !item.skipped);
      
      console.log('🚫 SIMPLE INTERFACE - Marking', pendingItems.length, 'items as skipped for topic:', currentTopic);
      
      // Mark each pending item as skipped
      for (const item of pendingItems) {
        await markAsDiscovered(item.queue_id, {
          skipped: true,
          skipped_at: Date.now()
        });
      }
      
      console.log('🚫 SIMPLE INTERFACE - Successfully skipped topic:', currentTopic);
      
      // Check if topic is now complete after skipping
      setTimeout(() => {
        if (currentTopic && isTopicComplete(currentTopic)) {
          console.log('🚫 SIMPLE INTERFACE - Topic complete after skipping, advancing:', currentTopic);
          
          setCurrentTopicIntroduced(false);
          populationAttempted.current.clear();
          
          // Set flag to prevent useEffect from double-populating
          setIsAdvancingTopic(true);
          
          advanceToNextTopicDirect().then((result) => {
            console.log('🚫 SIMPLE INTERFACE - Advanced after skip:', result);
            setTimeout(() => {
              setIsAdvancingTopic(false);
            }, 1000);
          }).catch((error) => {
            console.error('🚫 SIMPLE INTERFACE - Failed to advance after skip:', error);
            setIsAdvancingTopic(false);
          });
        }
      }, 500);
      
    } catch (error) {
      console.error('❌ SIMPLE INTERFACE - Error skipping discovery:', error);
    }
  }, [getItemsByTopic, markAsDiscovered, isTopicComplete, advanceToNextTopicDirect, setIsAdvancingTopic, setCurrentTopicIntroduced]);

  // Handle queue item confirmation
  const handleQueueItemConfirmed = React.useCallback(async (queueItem, userSelections) => {
    const itemId = queueItem.queue_id || queueItem.section_id;
    
    console.log('🔍 SIMPLE INTERFACE - handleQueueItemConfirmed called:', {
      itemId: itemId,
      queueItemTopic: queueItem.topic,
      queueItemSectionId: queueItem.section_id,
      userSelections: userSelections,
      hasSelections: userSelections && Array.isArray(userSelections.selections) ? userSelections.selections.length : 0,
      hasTemplateResult: userSelections && Array.isArray(userSelections.template_result) ? userSelections.template_result.length : 0,
      boolStr: userSelections ? userSelections.bool_str : 'none'
    });
    
    // Store current scroll position before making changes
    if (scrollContainerRef.current) {
      previousScrollTop.current = scrollContainerRef.current.scrollTop;
      console.log('💾 Stored scroll position before confirmation:', previousScrollTop.current);
    }
    
    // Preserve scroll position before adding new messages
    preserveScrollPositionRef.current = true;
    
    try {
      // Check if user made any selections - if not, just delete the queue item
      const hasSelections = userSelections.bool_str && userSelections.bool_str.trim() !== '' && userSelections.bool_str !== '(())';
      
      if (!hasSelections) {
        console.log('🗑️ SIMPLE INTERFACE - NO SELECTIONS MADE - Deleting queue item instead of saving empty item:', itemId);
        
        // Delete the queue item from central queue
        await centralDiscoveryQueue.removeQueueItem(itemId);
        
        return;
      }
      
      // Mark item as discovered
      console.log('🎯 SIMPLE INTERFACE - Marking item as discovered...');
      await markAsDiscovered(itemId, {
        ...userSelections,
        confirmed_at: Date.now()
      });

      console.log('🎯 SIMPLE INTERFACE - Item marked as discovered, waiting for database sync...');
      
      // Note: No confirmation message needed since we use direct callbacks
      
      // 🔧 FIX: Wait a bit longer for database save/load operations to complete, then check topic completion
      // Increased timeout to ensure database operations and queue state synchronization is complete
      setTimeout(() => {
        const currentTopic = currentDiscoveryState?.currentDiscoTopic;
        const topicItems = currentTopic ? getItemsByTopic(currentTopic) : [];
        const pendingItems = topicItems.filter(item => !item.disco_done && !item.skipped);
        
        console.log('🎯 SIMPLE INTERFACE - Checking topic completion after confirmation:', {
          currentTopic,
          totalItems: topicItems.length,
          pendingItems: pendingItems.length,
          completedItems: topicItems.filter(item => item.disco_done).length,
          skippedItems: topicItems.filter(item => item.skipped).length,
          isComplete: currentTopic ? isTopicComplete(currentTopic) : false,
          itemDetails: topicItems.map(item => ({
            section_id: item.section_id,
            disco_done: item.disco_done,
            skipped: item.skipped,
            queue_id: item.queue_id
          }))
        });
        
        // 🔧 ENHANCED: Force refresh queue state from central queue before final completion check
        // This ensures we have the most up-to-date state after markAsDiscovered
        const refreshedItems = currentTopic ? centralDiscoveryQueue.getItemsByTopic(currentTopic) : [];
        const refreshedPending = refreshedItems.filter(item => !item.disco_done && !item.skipped);
        
        console.log('🔄 SIMPLE INTERFACE - Refreshed queue state check:', {
          currentTopic,
          refreshedTotalItems: refreshedItems.length,
          refreshedPendingItems: refreshedPending.length,
          refreshedCompletedItems: refreshedItems.filter(item => item.disco_done).length,
          refreshedSkippedItems: refreshedItems.filter(item => item.skipped).length,
          isCompleteAfterRefresh: refreshedPending.length === 0 && refreshedItems.length > 0
        });
        
        // 🔧 FIX: Use refreshed state for completion check to avoid stale data issues
        const isCompleteUsingRefreshedData = refreshedPending.length === 0 && refreshedItems.length > 0;
        const isCompleteUsingHookCheck = currentTopic ? isTopicComplete(currentTopic) : false;
        
        console.log('🔧 SIMPLE INTERFACE - Final completion decision:', {
          isCompleteUsingRefreshedData,
          isCompleteUsingHookCheck,
          finalDecision: isCompleteUsingRefreshedData || isCompleteUsingHookCheck
        });
        
        if (currentTopic && (isCompleteUsingRefreshedData || isCompleteUsingHookCheck)) {
          console.log('🎯 SIMPLE INTERFACE - Topic complete, advancing:', currentTopic);
          
          setCurrentTopicIntroduced(false); // Reset for next topic
          populationAttempted.current.clear(); // Reset population attempts
          
          // Set flag to prevent useEffect from double-populating
          setIsAdvancingTopic(true);
          
          // Advance topic and call strategy for next topic (typing indicators handled automatically)
          console.log('🎯 SIMPLE INTERFACE - Calling advanceToNextTopicDirect...');
          const startTime = Date.now();
          advanceToNextTopicDirect().then((result) => {
            const endTime = Date.now();
            console.log('🎯 SIMPLE INTERFACE - advanceToNextTopicDirect result:', result, `(took ${endTime - startTime}ms)`);
            if (result && !result.allComplete) {
              console.log('🎯 SIMPLE INTERFACE - Successfully advanced and populated new topic');
            } else if (result && result.allComplete) {
              console.log('🎯 SIMPLE INTERFACE - All topics completed');
            }
            
            // Clear the advancing flag after a short delay
            setTimeout(() => {
              setIsAdvancingTopic(false);
            }, 1000);
          }).catch((error) => {
            console.error('🎯 SIMPLE INTERFACE - Failed to advance to next topic:', error);
            setIsAdvancingTopic(false);
          });
        } else {
          console.log('🎯 SIMPLE INTERFACE - Topic not yet complete - waiting for more items to be confirmed');
          
          // 🔧 FALLBACK: If topic still appears incomplete, force a queue refresh and try again
          console.log('🔄 SIMPLE INTERFACE - Attempting fallback queue refresh and re-check...');
          setTimeout(async () => {
            try {
              // Force reload the queue from database
              await centralDiscoveryQueue.initialize(centralDiscoveryQueue.sessionId);
              console.log('✅ SIMPLE INTERFACE - Queue refreshed, re-checking topic completion...');
              
              const refreshedTopicItems = currentTopic ? getItemsByTopic(currentTopic) : [];
              const refreshedPendingItems = refreshedTopicItems.filter(item => !item.disco_done);
              
              console.log('🔄 SIMPLE INTERFACE - After refresh check:', {
                currentTopic,
                totalItems: refreshedTopicItems.length,
                pendingItems: refreshedPendingItems.length,
                completedItems: refreshedTopicItems.filter(item => item.disco_done).length,
                isCompleteAfterRefresh: currentTopic ? isTopicComplete(currentTopic) : false
              });
              
              if (currentTopic && isTopicComplete(currentTopic)) {
                console.log('🎯 SIMPLE INTERFACE - Topic complete after refresh, advancing:', currentTopic);
                setCurrentTopicIntroduced(false);
                populationAttempted.current.clear();
                setIsAdvancingTopic(true);
                
                advanceToNextTopicDirect().then(() => {
                  setIsAdvancingTopic(false);
                }).catch(() => {
                  setIsAdvancingTopic(false);
                });
              }
            } catch (error) {
              console.error('❌ SIMPLE INTERFACE - Fallback refresh failed:', error);
            }
          }, 1000);
        }
      }, 1000); // Increased from 500ms to allow database save/load to complete
      
    } catch (error) {
      console.error('❌ SIMPLE INTERFACE - Error confirming item:', error);
    }
  }, [markAsDiscovered, currentDiscoveryState?.currentDiscoTopic, isTopicComplete, advanceToNextTopicDirect]);

  // Helper function to parse boolean strings
  const parseBooleanString = React.useCallback((boolStr) => {
    if (!boolStr) return [];
    
    try {
      // Special handling for location JSON format
      if (boolStr.startsWith('[{') && boolStr.endsWith('}]')) {
        console.log('🌍 PARSE: Detected location JSON format, parsing location objects');
        const locationObjects = JSON.parse(boolStr);
        return locationObjects.map((locObj, index) => ({
          label: locObj.address || `Location ${index + 1}`,
          value: locObj.address || `Location ${index + 1}`,
          originalValue: JSON.stringify(locObj)
        }));
      }
      
      // Standard OR/AND parsing for other formats
      // Extract content from parentheses
      const parenthesesMatch = boolStr.match(/\(+([^)]+)\)+/);
      const elementsStr = parenthesesMatch ? parenthesesMatch[1] : boolStr;
      
      // Split by OR and AND operators
      const elements = elementsStr.split(/\s+(OR|AND)\s+/);
      const values = elements.filter(element => element !== 'OR' && element !== 'AND');
      
      return values.map(element => {
        const trimmedElement = element.trim();
        
        // Check if element exists in queries mapping
        if (queries && queries[trimmedElement]) {
          return {
            label: unescape(queries[trimmedElement].name),
            value: trimmedElement,
            originalValue: trimmedElement
          };
        } else {
          // Check if it's a company field pattern
          const companyFieldMatch = trimmedElement.match(/workexp\.(recent|current)\.company\.name:"([^"]+)"/);
          if (companyFieldMatch) {
            // Extract company name from the boolean string
            const companyName = companyFieldMatch[2];
            console.log('🏢 SIMPLE: Extracted company name:', companyName, 'from:', trimmedElement);
            return {
              label: unescape(companyName),
              value: trimmedElement,
              originalValue: trimmedElement
            };
          } else {
            const eduFieldMatch = trimmedElement.match(/education\.school_name:"([^"]+)"/);
            if (eduFieldMatch) {
              const schoolName = eduFieldMatch[1];
              console.log('🎓 SIMPLE: Extracted school name:', schoolName, 'from:', trimmedElement);
              return {
                label: decodeURIComponent(schoolName),
                value: trimmedElement,
                originalValue: trimmedElement
              };              
            }
            return {
              label: decodeURIComponent(trimmedElement),
              value: trimmedElement,
              originalValue: trimmedElement
            };
          }
        }
      });
    } catch (error) {
      console.error('Error parsing boolean string:', error);
      return [];
    }
  }, [queries]);

  // Render template-based queue item
  const renderQueueItem = React.useCallback((queueItem, isEditable = true) => {
    const templateType = queueItem.template_type || DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR;
    const uxConfig = queueItem.ux_config || {};
    const templateProps = queueItem.template_props || {};
    const aiIntroMessage = queueItem.ai_intro_message || `Let's configure your ${queueItem.section_name || queueItem.topic} preferences.`;

    // Parse boolean string for data
    const extractedData = queueItem.bool_str ? parseBooleanString(queueItem.bool_str) : [];

    // Prepare template props
    let mergedProps = {
      ...templateProps,
      title: uxConfig.title || '', // Don't fallback to aiIntroMessage to avoid duplication
      confirmButtonText: uxConfig.confirm_button_text || 'Confirm Selection',
      readOnly: !isEditable
    };

    // Add allowEmptySelection from ux_config
    if (uxConfig.allow_empty_selection !== undefined) {
      mergedProps.allowEmptySelection = uxConfig.allow_empty_selection;
    }

    // Add skip button props for editable items
    if (isEditable) {
      mergedProps.onSkipDiscovery = () => handleSkipDiscovery(queueItem);
      mergedProps.showSkipButton = true;
      mergedProps.skipButtonText = `Skip ${queueItem.topic.charAt(0).toUpperCase() + queueItem.topic.slice(1)} Discovery`;
      
      mergedProps.onConfirmed = (result) => {
        console.log('🔍 SIMPLE INTERFACE - Template confirmed with result:', {
          result: result,
          queueItemId: queueItem.queue_id,
          queueItemSectionId: queueItem.section_id,
          queueItemTopic: queueItem.topic,
          templateType: queueItem.template_type
        });
        
        // Handle request to remove queue item (empty selection with allowEmptySelection)
        if (result && result.removeQueueItem === true) {
          console.log('🗑️ SIMPLE INTERFACE - Removing queue item due to empty selection');
          handleQueueItemConfirmed(queueItem, {
            selections: [],
            bool_str: '',
            template_result: [],
            originalValues: []
          });
          return;
        }
        
        // Extract selections from result object
        const selections = result && result.selections ? result.selections : result;
        
        // Process selections into boolean string format
        let boolStrParts = [];
        let displayNames = [];
        let finalBoolStr = '';
        
        // Special handling for INLINE_SLIDER template (experience range)
        if (templateType === DISCOVERY_TEMPLATE_TYPES.INLINE_SLIDER) {
          if (Array.isArray(selections) && selections.length === 2) {
            // Convert years to months and create range boolean string
            const minYears = selections[0];
            const maxYears = selections[1];
            const minMonths = minYears * 12;
            const maxMonths = maxYears * 12;
            
            finalBoolStr = `(workexp.overall_experience:${minMonths}-${maxMonths})`;
            displayNames = [`${minYears} - ${maxYears} years`];
            boolStrParts = [finalBoolStr];
            
            console.log('🎯 SIMPLE INTERFACE - INLINE_SLIDER range processed:', {
              yearsRange: selections,
              monthsRange: [minMonths, maxMonths],
              boolStr: finalBoolStr
            });
          }
        } else if (templateType === DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR && 
                   queueItem.topic === 'location' && queueItem.location_mapping) {
          // Special handling for location items - reconstruct JSON format
          if (Array.isArray(selections)) {
            const selectedLocationObjects = [];
            
            selections.forEach(selection => {
              const locationName = typeof selection === 'object' ? selection.label : selection;
              
              if (queueItem.location_mapping[locationName]) {
                selectedLocationObjects.push(queueItem.location_mapping[locationName]);
                displayNames.push(locationName);
              }
            });
            
            // Convert back to JSON format for bool_str
            if (selectedLocationObjects.length > 0) {
              finalBoolStr = JSON.stringify(selectedLocationObjects);
              console.log('🌍 SIMPLE INTERFACE - Location selection reconstructed:', {
                selections,
                selectedCount: selectedLocationObjects.length,
                boolStr: finalBoolStr.substring(0, 200) + '...'
              });
            }
          }
        } else if (templateType === DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR && 
                   queueItem.topic === 'skills') {
          // Special handling for skills - wrap in single quotes
          if (Array.isArray(selections)) {
            selections.forEach(selection => {
              const skillName = typeof selection === 'object' ? selection.label : selection;
              boolStrParts.push(`'${skillName}'`);
              displayNames.push(skillName);
            });
          } else if (typeof selections === 'string') {
            boolStrParts.push(`'${selections}'`);
            displayNames.push(selections);
          }
          
          finalBoolStr = boolStrParts.length > 0 ? `((${boolStrParts.join(' OR ')}))` : '';
          
          console.log('🎯 SKILLS PROCESSING - Formatted skills:', {
            originalSelections: selections,
            selectionsType: typeof selections,
            selectionsIsArray: Array.isArray(selections),
            selectionsLength: Array.isArray(selections) ? selections.length : 'not array',
            boolStrParts,
            finalBoolStr,
            displayNames
          });
        } else {
          // Standard processing for other template types
          if (Array.isArray(selections)) {
            selections.forEach(selection => {
              if (typeof selection === 'object' && selection.originalValue) {
                boolStrParts.push(selection.originalValue);
                displayNames.push(selection.label);
              } else {
                boolStrParts.push(selection);
                displayNames.push(selection);
              }
            });
          } else if (typeof selections === 'string') {
            boolStrParts.push(selections);
            displayNames.push(selections);
          }
          
          finalBoolStr = boolStrParts.length > 0 ? `((${boolStrParts.join(' OR ')}))` : '';
        }
        
        console.log('🔍 CALLING handleQueueItemConfirmed with:', {
          queueItemTopic: queueItem.topic,
          queueItemSectionId: queueItem.section_id,
          userSelectionsPayload: {
            selections: displayNames,
            bool_str: finalBoolStr,
            template_result: selections,
            originalValues: boolStrParts
          }
        });
        
        handleQueueItemConfirmed(queueItem, {
          selections: displayNames,
          bool_str: finalBoolStr,
          template_result: selections,
          originalValues: boolStrParts
        });
      };
    }

    // Set template-specific props
    switch (templateType) {
      case DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR:
        // CRITICAL FIX: Pass selectedItems for skills and other single-row selectors
        if (Array.isArray(templateProps.selectedItems)) {
          mergedProps.selectedItems = templateProps.selectedItems;
          console.log('🎯 SINGLE_ROW_TEMPLATE: Passing selectedItems for read-only display:', queueItem.section_id, templateProps.selectedItems.length, 'items');
        }
        // Ensure items are available for display
        if (!mergedProps.items || mergedProps.items.length === 0) {
          if (Array.isArray(extractedData) && extractedData.length > 0) {
            mergedProps.items = extractedData;
          } else if (Array.isArray(templateProps.selectedItems) && templateProps.selectedItems.length > 0) {
            // Use selectedItems as items for read-only display
            mergedProps.items = templateProps.selectedItems.map(item => 
              typeof item === 'object' ? (item.label || item.value || item) : item
            );
            console.log('🎯 SINGLE_ROW_TEMPLATE: Using selectedItems as items for completed template:', mergedProps.items);
          }
        }
        break;
        
      case DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR:
        if (!mergedProps.sections || mergedProps.sections.length === 0) {
          mergedProps.sections = [{
            title: queueItem.section_name || "Confirm Selection",
            items: extractedData.map(item => ({
              label: item.label || item,
              // CRITICAL FIX: For location items, use the label/value consistently, not originalValue
              // originalValue contains JSON which doesn't match selectedItems format
              value: item.value || item.label || item
            }))
          }];
        }
        if (!isEditable) {
          // For read-only display, set selectedItems based on stored template_props.selectedItems or parsed bool_str
          if (Array.isArray(templateProps.selectedItems) && templateProps.selectedItems.length > 0) {
            // Use stored selectedItems if available
            mergedProps.selectedItems = templateProps.selectedItems;
            console.log('🎯 MULTI_ROW_SELECTOR: Using stored selectedItems for read-only:', queueItem.section_id, {
              selectedItems: templateProps.selectedItems,
              selectedItemsTypes: templateProps.selectedItems.map(s => typeof s),
              sampleExtractedData: extractedData.slice(0, 2),
              topic: queueItem.topic
            });
          } else if (extractedData.length > 0 && mergedProps.sections) {
            // Fallback: use all parsed items from bool_str for read-only display
            const allItems = [];
            mergedProps.sections.forEach(section => {
              if (section.items) {
                section.items.forEach(item => {
                  allItems.push(item.value || item.label || item);
                });
              }
            });
            mergedProps.selectedItems = allItems;
            console.log('🎯 MULTI_ROW_SELECTOR: Using parsed bool_str items for read-only:', queueItem.section_id, {
              allItems,
              allItemsTypes: allItems.map(s => typeof s),
              sampleExtractedData: extractedData.slice(0, 2),
              topic: queueItem.topic
            });
          }
        }
        break;
        
      case DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART:
        // Use data from template_props if available (this contains the real queryIds and will get real percentages)
        if (templateProps.data) {
          mergedProps.data = templateProps.data;
        }
        mergedProps.chartTitle = uxConfig.chart_title || queueItem.section_name;
        mergedProps.chartSubtitle = uxConfig.chart_subtitle || "Distribution data";
        mergedProps.hide_zero_percentage = uxConfig.hide_zero_percentage;
        mergedProps.allowEmptySelection = uxConfig.allowEmptySelection;
        // In read-only mode, rely solely on template_props.selectedItems to show user-confirmed selections.
        break;
        
      default:
        mergedProps.items = extractedData;
        break;
    }

    // Hide read-only blocks that have no selections/content to avoid empty cards
    if (!isEditable) {
      let hasContent = true;
      if (templateType === DISCOVERY_TEMPLATE_TYPES.INLINE_SLIDER) {
        hasContent = true; // range selection is always meaningful when completed
      } else if (templateType === DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR) {
        const selected = Array.isArray(templateProps?.selectedItems) ? templateProps.selectedItems : [];
        hasContent = (Array.isArray(extractedData) && extractedData.length > 0) || selected.length > 0;
      } else if (templateType === DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART) {
        const selected = Array.isArray(templateProps?.selectedItems) ? templateProps.selectedItems : [];
        // For charts, only render read-only when we have explicit selections
        hasContent = selected.length > 0;
      } else if (templateType === DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR) {
        // SINGLE_ROW_SELECTOR - check both extractedData and selectedItems
        const selected = Array.isArray(templateProps?.selectedItems) ? templateProps.selectedItems : [];
        hasContent = (Array.isArray(extractedData) && extractedData.length > 0) || selected.length > 0;
      } else {
        // Other template types
        hasContent = Array.isArray(extractedData) && extractedData.length > 0;
      }
      if (!hasContent) {
        return null;
      }
    }

    // Render AI intro message
    const aiMessageComponent = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2, width: '100%' }}>
        <ChatBubble 
          message={aiIntroMessage}
          isAI={true}
          isFirstInSequence={true}
        />
      </Box>
    );

    // Render appropriate template
    let templateComponent;
    switch (templateType) {
      case DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR:
        templateComponent = <SingleRowSelectorTemplate {...mergedProps} />;
        break;
      case DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR:
        templateComponent = <MultiRowSelectorTemplate {...mergedProps} />;
        break;
      case DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART:
        templateComponent = <DistributionBarChartTemplate {...mergedProps} />;
        break;
      case DISCOVERY_TEMPLATE_TYPES.INLINE_SLIDER:
        templateComponent = <InlineSliderTemplate {...mergedProps} />;
        break;
      default:
        templateComponent = <SingleRowSelectorTemplate {...mergedProps} />;
    }

    return (
      <React.Fragment key={queueItem.section_id}>
        {aiMessageComponent}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start', 
          mb: 2, 
          width: '100%', 
          maxWidth: '90%',
          marginLeft: '48px' // Align with chat bubble content (AI icon + gap + bubble padding)
        }}>
          {templateComponent}
        </Box>
      </React.Fragment>
    );
  }, [handleQueueItemConfirmed, handleSkipDiscovery, parseBooleanString]);

  // Render a compact read-only stub for skipped items
  const renderSkippedStub = React.useCallback((queueItem) => {
    const label = queueItem.section_name || queueItem.topic || 'Section';
    const text = `Skipped: ${label}`;
    return (
      <React.Fragment key={`${queueItem.section_id || queueItem.queue_id}_skipped`}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2, width: '100%' }}>
          <ChatBubble
            message={text}
            isAI={true}
            isFirstInSequence={true}
          />
        </Box>
      </React.Fragment>
    );
  }, []);

  return (
    <Box
      ref={chatInterfaceRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        flex: 1,
        overflow: 'hidden',
        bgcolor: '#FFFFFF',
        borderRadius: '0px',
        padding: '0px',
        position: 'relative',
      }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          bgcolor: '#FFFFFF',
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingBottom: convoStarted ? '120px' : '20px',
          paddingTop: '20px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F9FAFB',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E5E7EB',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#D1D5DB',
          }
        }}
      >
        {/* Centered content container */}
        <Box sx={{ width: '100%', maxWidth: '800px', padding: '0 16px' }}>
          
          {/* Show initial messages from backend for non-started conversations */}
          {!convoStarted && messages.length > 0 && (
            <>
              {messages.filter(msg => msg.role === 'AI').map((message, index) => (
                <Box key={message.id || `initial_${index}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2, width: '100%' }}>
                  <ChatBubble 
                    message={message.content}
                    isAI={true}
                    isFirstInSequence={true}
                  />
                  {/* Show intake options after the "Ready to begin?" message */}
                  {message.content.includes("Ready to begin?") && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-start', 
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%', 
                      mt: 2,
                      marginLeft: '48px' // Align with chat bubble content
                    }}>
                      <Button
                        variant="contained"
                        onClick={handleStartIntake}
                        sx={{
                          backgroundColor: '#FFFFFF',
                          color: '#101828',
                          fontWeight: 400,
                          fontSize: '14px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #DCDFEA',
                          boxShadow: 'none',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                            boxShadow: 'none',
                          }
                        }}
                      >
                        Start Fresh
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => { if (typeof window.handleStartWithJD === 'function') window.handleStartWithJD(); }}
                        sx={{
                          backgroundColor: '#FFFFFF',
                          color: '#101828',
                          fontWeight: 400,
                          fontSize: '14px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #DCDFEA',
                          boxShadow: 'none',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#F9FAFB',
                            boxShadow: 'none',
                          }
                        }}
                      >
                        Start with JD
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => { if (typeof window.handleStartTopicBased === 'function') window.handleStartTopicBased(); }}
                        sx={{
                          borderColor: '#EAECF0',
                          color: '#101828',
                          fontWeight: 400,
                          fontSize: '14px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          textTransform: 'none',
                          '&:hover': { borderColor: '#98A2B3', color: '#101828', backgroundColor: '#F9FAFB' }
                        }}
                      >
                        Start From Scratch
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </>
          )}
          
          {/* Show conversation content when started */}
          {convoStarted && (
            <>
              {/* Render all conversation messages from backend */}
              {displayMessages.map((message, index) => (
                <Box key={message.id || `msg_${index}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: message.role === 'User' ? 'flex-end' : 'flex-start', mb: 2, width: '100%' }}>
                  <ChatBubble 
                    message={message.content}
                    isAI={message.role === 'AI'}
                    isFirstInSequence={index === 0 || displayMessages[index-1]?.role !== message.role}
                  />
                </Box>
              ))}

              {/* Thinking state moved to bottom - placeholder removed from here */}

              {/* Debug Info */}
              {false && process.env.NODE_ENV === 'development' && (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2">
                    Topic: {currentDiscoveryState?.currentDiscoTopic || 'None'} | 
                    Queue Items: {currentTopicItems.allItems.length} | 
                    Completed: {currentTopicItems.completedItems.length} | 
                    Pending: {currentTopicItems.pendingItems.length} |
                    AI Messages: {displayMessages.length}
                  </Typography>
                </Box>
              )}

              {/* Render ALL completed items as read-only (from all topics) */}
              {queueItems
                // Render read-only for completed OR skipped items (skipped renders as a stub)
                // Do NOT require template_type; fall back to a safe default renderer if missing
                .filter(item => ((item?.disco_done === true || item?.disco_done === 'true') || item?.skipped === true))
                // Do not render read-only blocks when there are no selected items/content
                .filter(item => {
                  // Always show a stub for skipped items, even with no selections/content
                  if (item.skipped === true) return true;

                  const templateType = item.template_type || DISCOVERY_TEMPLATE_TYPES.SINGLE_ROW_SELECTOR;

                  // Always allow experience range since it has an explicit value range
                  if (templateType === DISCOVERY_TEMPLATE_TYPES.INLINE_SLIDER) return true;

                  // For multi-row: allow rendering if sections carry items (even if bool_str/selectedItems are empty)
                  if (templateType === DISCOVERY_TEMPLATE_TYPES.MULTI_ROW_SELECTOR) {
                    const sections = Array.isArray(item.template_props?.sections) ? item.template_props.sections : [];
                    const sectionHasItems = sections.some(sec => Array.isArray(sec.items) && sec.items.length > 0);
                    if (sectionHasItems) return true;
                  }

                  // For chart: allow rendering if data exists or selections recorded
                  if (templateType === DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART) {
                    const data = Array.isArray(item.template_props?.data) ? item.template_props.data : [];
                    const selectedChart = Array.isArray(item.template_props?.selectedItems) ? item.template_props.selectedItems : [];
                    if (data.length > 0 || selectedChart.length > 0) return true;
                  }

                  // Prefer parsed bool_str selections
                  const extracted = item.bool_str ? parseBooleanString(item.bool_str) : [];
                  // Or fall back to template_props.selectedItems if present
                  const selected = Array.isArray(item.template_props?.selectedItems) ? item.template_props.selectedItems : [];

                  return (Array.isArray(extracted) && extracted.length > 0) || (selected.length > 0);
                })
                // If there is a pending (non-skipped) item for the same section_id, suppress the read-only duplicate
                .filter(item => {
                  const sid = item.section_id;
                  if (!sid) return true;
                  // Do not suppress on itself; only suppress if another item exists that is pending (not disco_done and not skipped)
                  return !queueItems.some(q => 
                    q.section_id === sid &&
                    q.topic === item.topic && // only consider duplicates within the same topic
                    q.queue_id !== item.queue_id &&
                    q.disco_done !== true &&
                    q.skipped !== true
                  );
                })
                // Deduplicate read-only blocks by (topic, section_id) to avoid cross-topic suppression
                .filter((item, idx, arr) => {
                  const key = `${item.topic || 'unknown'}::${item.section_id || item.queue_id || 'unknown'}`;
                  return arr.findIndex(it => `${it.topic || 'unknown'}::${it.section_id || it.queue_id || 'unknown'}` === key) === idx;
                })
                .map(item => {
                  try {
                    console.log('🛡️ READONLY RENDER GUARD:', {
                      section_id: item.section_id,
                      topic: item.topic,
                      disco_done: item.disco_done,
                      skipped: item.skipped,
                      hasTemplate: !!item.template_type
                    });
                  } catch (_) {}
                  return item.skipped === true ? renderSkippedStub(item) : renderQueueItem(item, false);
                })
              }

              {/* Render first pending item from current topic as interactive */}
              {currentTopicItems.nextPendingItem && (
                renderQueueItem(currentTopicItems.nextPendingItem, true)
              )}

              {/* Show progress */}
              {currentTopicItems.allItems.length > 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Progress: {currentTopicItems.completedItems.length} / {currentTopicItems.allItems.length - currentTopicItems.skippedItems.length} completed
                    {currentTopicItems.skippedItems.length > 0 && ` • ${currentTopicItems.skippedItems.length} skipped`}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* 🔧 FIX: Show thinking state inline after queue items (not overlay) */}
        {(isThinking || isWaitingForResponse) && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            mt: 2,
            mb: 2, 
            width: '100%'
          }}>
            <ThinkingAccordion 
              thinking={thinkingMessage || 'Processing your requirements...'}
              showThinking={true}
            />
          </Box>
        )}
      </Box>

      {/* Footer for manual input */}
      {convoStarted && (
        <Footer
          onSendText={async (text) => {
            // Store current scroll position before making changes
            if (scrollContainerRef.current) {
              previousScrollTop.current = scrollContainerRef.current.scrollTop;
              console.log('💾 Stored scroll position before sending text:', previousScrollTop.current);
            }
            
            // Preserve scroll position before adding new messages
            preserveScrollPositionRef.current = true;
            
            // Clear incomplete items when user sends message during discovery
            const currentTopic = currentDiscoveryState?.currentDiscoTopic;
            if (currentTopic && currentTopicItems.pendingItems.length > 0) {
              await populateTopicQueue(currentTopic);
            }
            // Call original onSendText
            if (onSendText) {
              onSendText(text);
            }
          }}
          onSendVoice={onSendVoice}
          onStopVoice={onStopVoice}
          isRecording={isRecording}
          disabled={isWaitingForResponse || !convoStarted || (currentTopicItems.nextPendingItem && currentTopicItems.nextPendingItem?.ux_config?.ro_footer !== false)}
          leftSidebarOpen={false}
          drawerWidth={0}
          collapsedWidth={0}
          rightSidebarOpen={false}
          rightSidebarWidth={0}
          placeholder={(!currentTopicItems.nextPendingItem || currentTopicItems.nextPendingItem?.ux_config?.ro_footer === false) ? "Add additional requirements or continue..." : "Please select from the options above..."}
        />
      )}
    </Box>
  );
};

export default ChatInterfaceQueueDrivenSimple;
